<script lang="ts" setup>
import './_index.scss'

import { computed } from 'vue'

import RoomieTag from '@lahaus-roomie/roomie/src/components/RoomieTag/index.vue'
import RoomieIcon from '@lahaus-roomie/roomie/src/components/RoomieIcon/index.vue'

import type { Props } from './types'
import PdFinancialInfo from '@/components/pd/FinancialInfoV2/index.vue'
import AppCarrousel from '@/components/App/CarrouselV2/index.vue'
import PdFeaturesSummary from '@/components/pd/FeaturesSummaryV2/index.vue'

import { getDeliveryTypeLabel } from '@/utils/project'

const { t } = useI18n()

const props = defineProps<Props>()

const emit = defineEmits(['openGallery'])

const filterImagesByType = (images: Picture[], imageType: string) => {
  return images.filter(image => image.type === imageType)
}

const sortImagesByOrder = (images: Picture[]) => {
  return images.sort((a, b) => a.order - b.order)
}

const extractImageUrls = (images: Picture[]) => {
  return images.map(image => image.url)
}

const getImages = (imageType: string) => {
  const images = props.listing.pictures_v2 || []
  const filteredImages = filterImagesByType(images, imageType)
  const sortedImages = sortImagesByOrder(filteredImages)
  return extractImageUrls(sortedImages)
}

const getCarrouselImages = () => {
  if (!props.listing.pictures_v2) return []
  return [props.listing.cover, ...getImages('gallery')]
}

const carrouselImages = computed(() => getCarrouselImages())

const itemsPerSlideDesktop = Math.min(carrouselImages.value.length, 3)

interface ImageSource {
  mediaWidth: number
  src: string
}

const generateImageSources = (url: string): ImageSource[] => {
  const imageSizes = [
    { mediaWidth: 767, height: 500 },
    { mediaWidth: 1023, height: 500 },
    { mediaWidth: 2500, height: 500 }
  ]

  return imageSizes.map(({ mediaWidth, height }) => ({
    mediaWidth,
    src: `${url}?auto=compress,enhance,format&h=${height}`
  }))
}

const carrouselImageSources = carrouselImages.value.map((url: string) => generateImageSources(url))

const dataAttributes = computed(() => {
  return {
    dataLh: { P: props.screen }
  }
})

const handleOpenGallery = (tabName: string) => {
  emit('openGallery', tabName)
}

</script>

<script lang="ts">
export default {
  name: 'PdHero'
}
</script>

<template>
  <section class="pd-hero">
    <div
      v-lh-id="{...dataAttributes.dataLh, Z: 'galeria principal', A: 'ver', C: 'fotos'}"
      class="pd-hero__gallery">
      <div
        class="pd-hero__gallery-info-container pointer-events-none">
        <div class="pd-hero__gallery-info-container-top">
          <div class="page-pd-v2-city-slug__section page-pd-v2-city-slug__section--padded py-16 lg:py-36 flex flex-row justify-between max-w-[1600px]">
            <div class="pointer-events-none flex flex-col lg:gap-4 lg:flex-col-reverse lg:items-start">
              <div class="flex flex-col gap-4 lg:flex-row-reverse lg:gap-8 lg:items-center">
                <RoomieTag
                  id="pd-hero-delivery-tag"
                  class="h-fit w-fit"
                  variant="information"
                  size="md"
                  :label="getDeliveryTypeLabel(listing.ims.delivered_at, t)">
                  <template #icon>
                    <RoomieIcon
                      icon-name="info" />
                  </template>
                </RoomieTag>

                <h1 class="text-28 lg:text-32 font-semibold">
                  {{ listing.name }}
                </h1>
              </div>

              <h3 class="text-14">
                {{ listing.neighborhood_label }}, {{ listing.city_label }}
              </h3>
            </div>

            <PdFinancialInfo
              class="hidden lg:flex"
              :listing="props.listing" />
          </div>
        </div>
      </div>

      <AppCarrousel
        id="pd-hero-desktop"
        class="pd-hero__gallery-carrousel hidden lg:flex"
        :pictures="carrouselImages"
        :pictures-sources="carrouselImageSources"
        picture-container-styles="flex gap-4"
        anchor-styles="w-full h-full flex"
        img-styles="object-cover w-full h-full -z-1"
        dots-styles="!bottom-12 z-40"
        :data-attributes="{...dataAttributes, dataLh: {...dataAttributes.dataLh, Z: 'galeria principal'}}"
        :items-per-slide="itemsPerSlideDesktop"
        navigation-button-size="lg"
        @image-clicked="emit('openGallery', 'pictures')" />

      <AppCarrousel
        id="pd-hero-mobile"
        class="pd-hero__gallery-carrousel flex lg:hidden"
        :pictures="carrouselImages"
        :pictures-sources="carrouselImageSources"
        :data-attributes="{...dataAttributes, dataLh: {...dataAttributes.dataLh, Z: 'galeria principal'}}"
        picture-container-styles="flex gap-4 px-28"
        anchor-styles="w-full h-full flex"
        img-styles="object-cover w-full h-full -z-1"
        dots-styles="dot-styles"
        :items-per-slide="1"
        navigation-button-size="lg"
        @image-clicked="emit('openGallery', 'pictures')" />

      <PdFeaturesSummary
        v-if="showFeaturesSummary"
        :listing
        :data-attributes="{...dataAttributes, dataLh: {...dataAttributes.dataLh, Z: 'galeria'}}"
        @open-gallery="handleOpenGallery" />

      <PdFinancialInfo
        class="flex lg:hidden px-24 pt-24 pb-0 lg:pb-16"
        :listing="props.listing" />
    </div>
  </section>
</template>

<i18n src="./i18n.json" lang="json"></i18n>
